import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastProvider } from '@bravemove/ui-web';
import { AuthProvider } from './auth/AuthProvider';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { AdminShell } from './layout/AdminShell';
import { LoginPage } from './pages/LoginPage';
import { LoginTotpPage } from './pages/LoginTotpPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { TotpEnrollPage } from './pages/TotpEnrollPage';
import { DashboardHome } from './pages/DashboardHome';
import { AdminVenuesPage } from './pages/AdminVenuesPage';
import { AdminVenueCreatePage } from './pages/AdminVenueCreatePage';
import { AdminVenueDetailPage } from './pages/AdminVenueDetailPage';
import { MergeWizardPage } from './pages/venues/MergeWizardPage';
import { AdminVenueSubmissionsQueuePage } from './pages/AdminVenueSubmissionsQueuePage';
import { AdminVenueSubmissionReviewPage } from './pages/AdminVenueSubmissionReviewPage';
import { AdminVenueEditsQueuePage } from './pages/AdminVenueEditsQueuePage';
import { AdminVenueEditReviewPage } from './pages/AdminVenueEditReviewPage';
import { FinancialPage } from './pages/FinancialPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { OpsLogsPage } from './pages/ops/logs';
import { OpsHealthPage } from './pages/ops/health';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
// F09 v2 — /verifications module (requests / candidates / watchlist tabs,
// request detail, entity page, direct verify, QA) + the legacy redirects.
import { VerificationsPage } from './pages/verifications/index';
import { VerificationRequestDetailPage } from './pages/verifications/requests/[id]';
import { VerificationEntityPage } from './pages/verifications/entities/[type]/[id]';
import { VerificationDirectVerifyPage } from './pages/verifications/direct';
import { VerificationQaPage } from './pages/verifications/qa';
import {
  LegacyUserVerifyRedirect,
  LegacyVerificationDetailRedirect,
} from './pages/verifications/redirects';
import { VenueClaimsQueuePage } from './pages/VenueClaimsQueuePage';
import { VenueClaimReviewPage } from './pages/VenueClaimReviewPage';
import { B2bAccountsListPage } from './pages/b2b-accounts/B2bAccountsListPage';
import { B2bAccountDetailPage } from './pages/b2b-accounts/B2bAccountDetailPage';
import { VenuePhotoQueuePage } from './pages/venue-photos/VenuePhotoQueuePage';
import { VenuePhotoReviewPage } from './pages/venue-photos/VenuePhotoReviewPage';
// F17 (Wave 2C) — admin booking detail + platform cancellation policy.
import { AdminBookingDetailPage } from './pages/bookings/[id]';
// Admin bookings list (read-only). Reachable globally (Venues nav group) and as
// the F42 venue-detail "Bookings" read-through target (`/bookings?venueId=`).
import { AdminBookingsPage } from './pages/bookings/index';
import { AdminCancellationPolicyPage } from './pages/cancellation-policy';
// F20 (Wave 2D) — admin payment-disputes queue + detail + activity feed.
import { PaymentDisputesQueuePage } from './pages/payment-disputes/index';
import { PaymentDisputeDetailPage } from './pages/payment-disputes/[id]';
import { PaymentsActivityFeedPage } from './pages/payments/index';
// F21 (Wave 2D) — admin per-venue payment-config detail + override pages.
import { AdminVenuePaymentConfigPage } from './pages/venues/[venueId]/payment-config/index';
import { AdminVenuePaymentConfigOverridePage } from './pages/venues/[venueId]/payment-config/override';
// F23 (Wave 2C) — admin commission rates + statements queue + detail pages.
import { CommissionRatesPage } from './pages/commission-rates/index';
import { CommissionStatementsPage } from './pages/commission-statements/index';
import { CommissionStatementDetailPage } from './pages/commission-statements/[statementId]';
// F23 (CST-003) — remounted per-venue commission view (rate history +
// statements); reachable from the venue-detail Commission deep-link card.
import { AdminVenueCommissionPage } from './pages/venues/[venueId]/commission';
// F26 (Wave 2D) — admin notifications dry-run + test-send (super_admin tools).
// The admin in-app inbox + preferences pages were removed: the F26 inbox is
// consumer/B2B-only (recipient_user_id FKs users.id, not admins). See AdminTopNav.
import { AdminNotificationsDryRunPage } from './pages/admin/notifications/dry-run';
import { AdminNotificationsTestSendPage } from './pages/admin/notifications/test-send';
// F28 (Wave 2C-ii) — admin email-suppressions list page.
import { EmailSuppressionsPage } from './pages/email-suppressions/index';
// F27 (Wave 2E) — admin WhatsApp templates registry + quality state pages.
import { WhatsAppTemplatesPage } from './pages/whatsapp/templates/index';
import { WhatsAppQualityPage } from './pages/whatsapp/quality/index';
// F29 (Wave 2C) — admin team moderation queue + detail pages.
import { AdminTeamsPage } from './pages/teams/index';
import { AdminTeamDetailPage } from './pages/teams/[teamId]';
// F31 (Wave 2B) — skill-based matching admin surface: skill-disputes queue +
// detail (resolve), sport-skill-descriptors CMS, per-user skill-ratings +
// override. Read perms gate the pages; write/resolve/override actions are
// additionally gated inside the pages (server re-enforces).
import { AdminSkillDisputesPage } from './pages/skill-disputes/index';
import { AdminSkillDisputeDetailPage } from './pages/skill-disputes/[id]';
import { AdminSportSkillDescriptorsPage } from './pages/sport-skill-descriptors/index';
import { AdminUserSkillRatingsPage } from './pages/users/[userId]/skill-ratings';
// F32 (Wave 2C) — open-games marketplace moderation: list + detail
// (suspend/unsuspend gated on `open_games:moderate`) + report queue (resolve
// gated on `open_games:moderate`). Read perm `open_games:read` gates the pages;
// the in-page action bars are additionally gated (server re-enforces).
import { AdminOpenGamesPage } from './pages/open-games/index';
import { AdminOpenGameDetailPage } from './pages/open-games/[id]';
import { AdminOpenGameReportsPage } from './pages/open-game-reports/index';
// F33 (Wave 2D) — trainer cert-verification queue + moderation detail. Read +
// write actions all gated on the single `trainer_verifications:review`
// permission (super_admin + content_moderator); the server re-enforces.
import { TrainerVerificationsQueuePage } from './pages/trainer-verifications/index';
import { TrainerVerificationDetailPage } from './pages/trainer-verifications/[userId]';
// F34 (Wave 2D) — trainer package-consumption reversal worklist (the single
// F34 admin touch). Lookup-by-id + reverse, gated on
// `trainer_package_consumption:reverse` (super_admin + support); server
// re-enforces.
import { TrainerPackageConsumptionsPage } from './pages/trainer-package-consumptions/index';
// F35 (Wave 2F) — per-trainer billing page (view active plan + effective rate +
// history + inline invoices; override plan/rate/trial gated on
// `trainer_billing:manage`). View is gated on `payments:read`; the override
// CTA is hidden without `trainer_billing:manage`.
import { AdminTrainerBillingPage } from './pages/trainers/[trainerUserId]/billing';
// F36 (Wave 2E) — club moderation: list + detail (roster/teams/audit tabs +
// verify/suspend/reinstate dialogs gated on `clubs:moderate`). Read-gated on
// `clubs:read` (super_admin + support + content_moderator); the in-page action
// bar is additionally gated on `clubs:moderate` (super_admin +
// content_moderator); the server re-enforces.
import { AdminClubsPage } from './pages/clubs/index';
import { AdminClubDetailPage } from './pages/clubs/[clubId]';
// F37 (Wave 2D) — player↔trainer recommendations admin surface: badge + review-
// tag catalog CMS (read gates the pages; Add/Edit/Delete gated inside on
// `recommendation_catalog:write`) + the review/endorsement moderation queue +
// detail (moderate gated on `reviews:moderate` / `endorsements:moderate`); the
// server re-enforces + audits via the F06 onSend hook.
import { AdminBadgeCatalogPage } from './pages/recommendation-catalog/badges/index';
import { AdminReviewTagsPage } from './pages/recommendation-catalog/tags/index';
import { ReviewModerationQueuePage } from './pages/review-moderation/index';
import { ReviewModerationDetailPage } from './pages/review-moderation/[id]';
// F43 (Track B) — admin user management & moderation. The consumer-user
// management surface (list at /users — replaces the F06 placeholder; detail at
// /users/:id — the deep-link target for `subject_type='user'`), the central
// report queue (UNION of user_reports ∪ legacy open_game_reports) + detail (incl.
// the scoped ±20 chat-context viewer for `subject_type='chat_message'`), and the
// GDPR request-tracking processor. Read-gated on users:read / reports:read /
// gdpr_requests:read; the write affordances are additionally gated inside the
// pages (users:suspend / users:note / users:device_revoke / reports:resolve /
// chat:view_reported / chat:moderate / users:bulk / gdpr_requests:process).
// Server re-enforces every write.
import { AdminPlatformUsersPage } from './pages/platform-users/index';
import { AdminPlatformUserDetailPage } from './pages/platform-users/[userId]';
import { AdminReportsQueuePage } from './pages/reports/index';
import { AdminReportDetailPage } from './pages/reports/[id]';
import { AdminGdprPage } from './pages/gdpr/index';
// F38 (Wave 2 Track D) — B2B SaaS subscription tiers. The tier-catalog pricing
// editor (read-gated on `subscription_tiers:read`; Edit pricing gated inside on
// `subscription_tiers:write`), the venue-subscriptions list + per-venue detail
// (read-gated on `payments:read`; override/grant-trial/manual-invoice gated
// inside on `venue_subscription:override`). Subscription invoices ride the F23
// commission-statements queue (its `source` filter gains venue_subscription +
// venue_hybrid). Server re-enforces every write.
import { SubscriptionTiersPage } from './pages/subscription-tiers/index';
import { VenueSubscriptionsPage } from './pages/venue-subscriptions/index';
import { VenueSubscriptionDetailPage } from './pages/venue-subscriptions/[venueId]';
// F39 (Wave 2C) — Featured placements admin surface. Inventory CMS (read-gated
// on `placement_inventory:read`; Edit gated inside on `placement_inventory:write`),
// the pre-approval FIFO queue + detail (read-gated `placement_approvals:review`;
// Approve/Reject inside on `placement_approvals:approve`), the cross-venue
// active monitor (override-cancel inside on `placement_approvals:approve`;
// credit-grant inside on `placement_credits:grant`), and the platform
// analytics surface. Server re-enforces every write.
import { PlacementInventoryPage } from './pages/placement-inventory/index';
import { PlacementApprovalsPage } from './pages/placement-approvals/index';
import { PlacementDetailPage } from './pages/placement-approvals/[id]';
import { PlacementMonitorPage } from './pages/placements/index';
import { PlacementAnalyticsPage } from './pages/placements/analytics';
// F40 (Wave 2 Track D — admin) — Promotions moderation surface. Cross-venue
// moderation list + filters (read-gated on `promotions:read`), the per-promo
// detail with metrics + redemption ledger (Suspend/Restore inside additionally
// gated on `promotions:moderate` — finance + support are read-only), and the
// platform analytics surface. The static `/promotions/analytics` route MUST
// precede the dynamic `/promotions/:id` route below. Server re-enforces.
import { AdminPromotionsListPage } from './pages/promotions/index';
import { AdminPromotionDetailPage } from './pages/promotions/[id]';
import { AdminPromotionAnalyticsPage } from './pages/promotions/analytics';
// F41 (Wave 2 Track D — admin) — Tournament & event listings moderation. Cross-
// org moderation list + filters + KPIs (read-gated on `tournaments:read`), the
// per-tournament detail with BracketView + suspend/unsuspend/edit/cancel/force-
// refund (gated inside on `tournaments:moderate`), the organizer-approval
// surface (gated on `tournaments:approve_organizer`), and the platform analytics
// surface. The static `/tournaments/analytics` route MUST precede the dynamic
// `/tournaments/:id` route below. Server re-enforces every write.
import { AdminTournamentsPage } from './pages/tournaments/index';
import { AdminTournamentDetailPage } from './pages/tournaments/[id]';
import { AdminTournamentAnalyticsPage } from './pages/tournaments/analytics';
import { AdminOrganizersPage } from './pages/organizers/index';
// F44 (Wave 2D) — Admin Financial Dashboard. The whole /finance module is gated
// on `financial:read` (finance + super_admin); the dashboard home + settlements
// + refunds + exports + alert-rules pages. Export generation + alert-rule edits
// are additionally gated INSIDE the pages on `financial:export`. The base route
// is `/finance` (NOT `/financial` — that path is F06's currency-rate editor).
// The API paths stay `/admin/financial/*`. Server re-enforces every read/write.
import { FinanceDashboardPage } from './pages/finance/index';
import { FinanceSettlementsPage } from './pages/finance/settlements';
import { FinanceRefundsPage } from './pages/finance/refunds';
import { FinanceExportsPage } from './pages/finance/exports';
import { FinanceAlertRulesPage } from './pages/finance/alert-rules';
// F45 (Wave 2D) — Admin Analytics & Reporting. The whole /analytics module is
// read-only, gated on `analytics:read` (all 4 admin roles — D4). Editing alert
// thresholds is additionally gated INSIDE the alert-rules page on
// `analytics:manage_alerts` (super_admin only). All routes are static siblings
// except the scorecard routes (specific `/analytics/scorecards/{venues|sports|
// trainers}/:id`); no param-route precedence concern. Server re-enforces.
import { AnalyticsDashboardPage } from './pages/analytics/index';
import { AnalyticsFunnelsPage } from './pages/analytics/funnels';
import { AnalyticsRetentionPage } from './pages/analytics/retention';
import { AnalyticsScorecardPage } from './pages/analytics/scorecards';
import { AnalyticsGeographicPage } from './pages/analytics/geographic';
import { AnalyticsChurnPage } from './pages/analytics/churn';
import { AnalyticsNotificationsPage } from './pages/analytics/notifications';
import { AnalyticsAlertRulesPage } from './pages/analytics/alert-rules';

/**
 * F06 — Admin SPA root.
 *
 * Composition order:
 *   <ToastProvider>     — global toast portal
 *     <BrowserRouter>   — history + URL state
 *       <AuthProvider>  — refresh-cookie hydration + token state
 *         <Routes />    — public auth + protected /admin/** routes
 *
 * `<BraveMoveThemeProvider>`, `<I18nProvider>` and the inert F09
 * `<VerifiedTickProvider>` wrap us from `main.tsx`.
 */
export function App() {
  return (
    <ToastProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public auth routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/totp" element={<LoginTotpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
            <Route path="/totp-enroll" element={<TotpEnrollPage />} />

            {/* Protected admin shell + child routes */}
            <Route
              element={
                <ProtectedRoute>
                  <AdminShell />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<DashboardHome />} />

              {/* F10 — Venue catalogue */}
              <Route
                path="/venues"
                element={
                  <ProtectedRoute permission="venues:read">
                    <AdminVenuesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/new"
                element={
                  <ProtectedRoute permission="venues:write">
                    <AdminVenueCreatePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/submissions"
                element={
                  <ProtectedRoute permission="venues:moderate">
                    <AdminVenueSubmissionsQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/submissions/:id"
                element={
                  <ProtectedRoute permission="venues:moderate">
                    <AdminVenueSubmissionReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/edits"
                element={
                  <ProtectedRoute permission="venues:moderate">
                    <AdminVenueEditsQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/edits/:id"
                element={
                  <ProtectedRoute permission="venues:moderate">
                    <AdminVenueEditReviewPage />
                  </ProtectedRoute>
                }
              />
              {/* F21 — per-venue payment-config detail + override.
                  Must come BEFORE /venues/:id so the more-specific paths win. */}
              <Route
                path="/venues/:venueId/payment-config"
                element={
                  <ProtectedRoute permission="payments:read">
                    <AdminVenuePaymentConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/:venueId/payment-config/override"
                element={
                  <ProtectedRoute permission="payment_config:write">
                    <AdminVenuePaymentConfigOverridePage />
                  </ProtectedRoute>
                }
              />
              {/* F23 (CST-003) — remount of the orphaned per-venue Commission
                  view (rate history + statements). Must come BEFORE the
                  dynamic /venues/:id route so the more-specific path wins. */}
              <Route
                path="/venues/:venueId/commission"
                element={
                  <ProtectedRoute permission="commission_rate:read">
                    <AdminVenueCommissionPage />
                  </ProtectedRoute>
                }
              />
              {/* F42 — merge wizard. Static '/venues/merge*' must precede the
                  dynamic '/venues/:id' (React Router ranks static higher; the
                  explicit ordering keeps it obvious). */}
              <Route
                path="/venues/merge"
                element={
                  <ProtectedRoute permission="venues:merge">
                    <MergeWizardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venues/merge/:canonicalId/:sourceId"
                element={
                  <ProtectedRoute permission="venues:merge">
                    <MergeWizardPage />
                  </ProtectedRoute>
                }
              />

              {/* /venues/:id must come AFTER the static sub-routes above */}
              <Route
                path="/venues/:id"
                element={
                  <ProtectedRoute permission="venues:read">
                    <AdminVenueDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F43 — consumer-user management list (replaces the F06
                  placeholder). The static `/users/:userId/skill-ratings` and
                  `/users/:userId/verify` routes below are ranked higher by
                  React Router than the dynamic `/users/:id` detail route. */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute permission="users:read">
                    <AdminPlatformUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/:id"
                element={
                  <ProtectedRoute permission="users:read">
                    <AdminPlatformUserDetailPage />
                  </ProtectedRoute>
                }
              />
              {/* F43 — central report queue + detail (incl. the scoped chat-
                  context viewer for chat_message subjects). */}
              <Route
                path="/reports"
                element={
                  <ProtectedRoute permission="reports:read">
                    <AdminReportsQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/:id"
                element={
                  <ProtectedRoute permission="reports:read">
                    <AdminReportDetailPage />
                  </ProtectedRoute>
                }
              />
              {/* F43 — GDPR request-tracking processor (scaffolding). */}
              <Route
                path="/gdpr"
                element={
                  <ProtectedRoute permission="gdpr_requests:read">
                    <AdminGdprPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/financial"
                element={
                  <ProtectedRoute permission="currency:read">
                    <FinancialPage />
                  </ProtectedRoute>
                }
              />
              {/* F45 — Admin Analytics & Reporting. Read-only, gated on
                  `analytics:read` (all 4 admin roles). The dashboard + 6 panel
                  sub-pages + the 3 scorecard routes. Editing alert thresholds is
                  additionally gated inside the alert-rules page on
                  `analytics:manage_alerts` (super_admin). Static scorecard paths
                  carry no param-route precedence concern. Server re-enforces. */}
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/funnels"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsFunnelsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/retention"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsRetentionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/geographic"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsGeographicPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/churn"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsChurnPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/notifications"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsNotificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/alert-rules"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsAlertRulesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/scorecards/venues/:id"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsScorecardPage entity="venues" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/scorecards/sports/:id"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsScorecardPage entity="sports" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics/scorecards/trainers/:id"
                element={
                  <ProtectedRoute permission="analytics:read">
                    <AnalyticsScorecardPage entity="trainers" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin-users"
                element={
                  <ProtectedRoute permission="admin_users:read">
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit-log"
                element={
                  <ProtectedRoute permission="audit:read">
                    <AuditLogPage />
                  </ProtectedRoute>
                }
              />
              {/* F60 — production API logs + health, super_admin only
                  (`ops_logs:read`). Server re-enforces. */}
              <Route
                path="/ops/logs"
                element={
                  <ProtectedRoute permission="ops_logs:read">
                    <OpsLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ops/health"
                element={
                  <ProtectedRoute permission="ops_logs:read">
                    <OpsHealthPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/account" element={<AccountSettingsPage />} />
              {/* F09 v2 — verifications module. Everything is gated on
                  `verifications:read`; decisions inside the pages need
                  `verifications:review`; direct verify is super_admin
                  (`verifications:issue_privileged`). Static segments
                  (requests / entities / direct / qa) outrank `/:id`. */}
              <Route
                path="/verifications"
                element={
                  <ProtectedRoute permission="verifications:read">
                    <VerificationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verifications/requests/:id"
                element={
                  <ProtectedRoute permission="verifications:read">
                    <VerificationRequestDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verifications/entities/:type/:id"
                element={
                  <ProtectedRoute permission="verifications:read">
                    <VerificationEntityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verifications/direct"
                element={
                  <ProtectedRoute permission="verifications:read">
                    <ProtectedRoute permission="verifications:issue_privileged">
                      <VerificationDirectVerifyPage />
                    </ProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verifications/qa"
                element={
                  <ProtectedRoute permission="verifications:read">
                    <VerificationQaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/verifications/:id"
                element={
                  <ProtectedRoute permission="verifications:read">
                    <LegacyVerificationDetailRedirect />
                  </ProtectedRoute>
                }
              />
              {/* Retired v1 privileged-issue page → direct verify (plan §6). */}
              <Route
                path="/users/:userId/verify"
                element={
                  <ProtectedRoute permission="verifications:issue_privileged">
                    <LegacyUserVerifyRedirect />
                  </ProtectedRoute>
                }
              />

              {/* F11 — Venue claim review */}
              <Route
                path="/venue-claims"
                element={
                  <ProtectedRoute permission="venue_claims:read">
                    <VenueClaimsQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venue-claims/:id"
                element={
                  <ProtectedRoute permission="venue_claims:read">
                    <VenueClaimReviewPage />
                  </ProtectedRoute>
                }
              />

              {/* F11 — B2B accounts management (Task 19) */}
              <Route
                path="/b2b-accounts"
                element={
                  <ProtectedRoute permission="b2b_accounts:read">
                    <B2bAccountsListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/b2b-accounts/:id"
                element={
                  <ProtectedRoute permission="b2b_accounts:read">
                    <B2bAccountDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F12 — Venue photo moderation */}
              <Route
                path="/venue-photos"
                element={
                  <ProtectedRoute permission="venue_photos:moderate">
                    <VenuePhotoQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venue-photos/:photoId"
                element={
                  <ProtectedRoute permission="venue_photos:moderate">
                    <VenuePhotoReviewPage />
                  </ProtectedRoute>
                }
              />

              {/* F16/F17 — Booking lifecycle admin. The static `/bookings` list
                  must precede the dynamic `/bookings/:id` detail route. */}
              <Route
                path="/bookings"
                element={
                  <ProtectedRoute permission="bookings:read">
                    <AdminBookingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bookings/:id"
                element={
                  <ProtectedRoute permission="bookings:read">
                    <AdminBookingDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cancellation-policy"
                element={
                  <ProtectedRoute permission="bookings:edit_cancellation_policy">
                    <AdminCancellationPolicyPage />
                  </ProtectedRoute>
                }
              />

              {/* F20 — Payment disputes (queue + detail) + payments activity feed */}
              <Route
                path="/payment-disputes"
                element={
                  <ProtectedRoute permission="payment_disputes:read">
                    <PaymentDisputesQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-disputes/:id"
                element={
                  <ProtectedRoute permission="payment_disputes:read">
                    <PaymentDisputeDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments"
                element={
                  <ProtectedRoute permission="payments:read">
                    <PaymentsActivityFeedPage />
                  </ProtectedRoute>
                }
              />

              {/* F23 — Variable commission engine (rates + statements queue + detail) */}
              <Route
                path="/commission-rates"
                element={
                  <ProtectedRoute permission="commission_rate:read">
                    <CommissionRatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/commission-statements"
                element={
                  <ProtectedRoute permission="payments:read">
                    <CommissionStatementsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/commission-statements/:statementId"
                element={
                  <ProtectedRoute permission="payments:read">
                    <CommissionStatementDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F38 — B2B SaaS subscription tiers. Tier-catalog pricing editor
                  (read-gated `subscription_tiers:read`; Edit pricing inside on
                  `subscription_tiers:write`), venue-subscriptions list + detail
                  (read-gated `payments:read`; override/grant-trial/manual-invoice
                  inside on `venue_subscription:override`). Server re-enforces. */}
              <Route
                path="/subscription-tiers"
                element={
                  <ProtectedRoute permission="subscription_tiers:read">
                    <SubscriptionTiersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venue-subscriptions"
                element={
                  <ProtectedRoute permission="payments:read">
                    <VenueSubscriptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/venue-subscriptions/:venueId"
                element={
                  <ProtectedRoute permission="payments:read">
                    <VenueSubscriptionDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F39 — Featured placements. Inventory CMS (read-gated on
                  `placement_inventory:read`; Edit inside on
                  `placement_inventory:write`), the pre-approval queue + detail
                  (read-gated `placement_approvals:review`; Approve/Reject inside
                  on `placement_approvals:approve`), the cross-venue monitor
                  (read-gated `placement_inventory:read`; cancel inside on
                  `placement_approvals:approve`; credit-grant inside on
                  `placement_credits:grant`), and the analytics surface. The
                  static `/placements/analytics` route MUST precede the dynamic
                  monitor route below. Server re-enforces. */}
              <Route
                path="/placement-inventory"
                element={
                  <ProtectedRoute permission="placement_inventory:read">
                    <PlacementInventoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/placement-approvals"
                element={
                  <ProtectedRoute permission="placement_approvals:review">
                    <PlacementApprovalsPage />
                  </ProtectedRoute>
                }
              />
              {/* rev-13 — the detail READ is relaxed to placement_inventory:read
                  (matches the backend GET) so support + finance can open rows
                  they can already list from /placements; Approve/Reject/Cancel
                  stay double-gated on placement_approvals:approve in-page +
                  server-side. */}
              <Route
                path="/placement-approvals/:id"
                element={
                  <ProtectedRoute permission="placement_inventory:read">
                    <PlacementDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/placements/analytics"
                element={
                  <ProtectedRoute permission="placement_inventory:read">
                    <PlacementAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/placements"
                element={
                  <ProtectedRoute permission="placement_inventory:read">
                    <PlacementMonitorPage />
                  </ProtectedRoute>
                }
              />

              {/* F40 — Promotions moderation. Cross-venue list + filters
                  (read-gated on `promotions:read`), the per-promo detail with
                  metrics + redemption ledger (Suspend/Restore inside gated on
                  `promotions:moderate`), and the platform analytics surface. The
                  static `/promotions/analytics` route MUST precede the dynamic
                  `/promotions/:id` route. Server re-enforces every write. */}
              <Route
                path="/promotions/analytics"
                element={
                  <ProtectedRoute permission="promotions:read">
                    <AdminPromotionAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/promotions/:id"
                element={
                  <ProtectedRoute permission="promotions:read">
                    <AdminPromotionDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/promotions"
                element={
                  <ProtectedRoute permission="promotions:read">
                    <AdminPromotionsListPage />
                  </ProtectedRoute>
                }
              />

              {/* F41 — Tournament & event listings moderation. The static
                  `/tournaments/analytics` route MUST precede the dynamic
                  `/tournaments/:id` route. The organizers approval surface is
                  gated on `tournaments:approve_organizer`. Server re-enforces. */}
              <Route
                path="/tournaments/analytics"
                element={
                  <ProtectedRoute permission="tournaments:read">
                    <AdminTournamentAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tournaments/:id"
                element={
                  <ProtectedRoute permission="tournaments:read">
                    <AdminTournamentDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tournaments"
                element={
                  <ProtectedRoute permission="tournaments:read">
                    <AdminTournamentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/organizers"
                element={
                  <ProtectedRoute permission="tournaments:approve_organizer">
                    <AdminOrganizersPage />
                  </ProtectedRoute>
                }
              />

              {/* F44 — Admin Financial Dashboard. The whole module is gated on
                  `financial:read` (finance + super_admin). Export generation +
                  alert-rule edits are additionally gated INSIDE the pages on
                  `financial:export`. Base route `/finance` (NOT `/financial` —
                  that is F06's currency editor). All static sibling paths — no
                  param-route precedence concern. */}
              <Route
                path="/finance"
                element={
                  <ProtectedRoute permission="financial:read">
                    <FinanceDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/settlements"
                element={
                  <ProtectedRoute permission="financial:read">
                    <FinanceSettlementsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/refunds"
                element={
                  <ProtectedRoute permission="financial:read">
                    <FinanceRefundsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/exports"
                element={
                  <ProtectedRoute permission="financial:read">
                    <FinanceExportsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/finance/alert-rules"
                element={
                  <ProtectedRoute permission="financial:read">
                    <FinanceAlertRulesPage />
                  </ProtectedRoute>
                }
              />

              {/* F26 — admin notification tooling: dry-run + test-send
                  (super_admin only via `notifications:dry_run`). The admin
                  in-app inbox + preferences routes were removed — the inbox is
                  consumer/B2B-only at MVP (see AdminTopNav). */}
              <Route
                path="/admin/notifications/dry-run"
                element={
                  <ProtectedRoute permission="notifications:dry_run">
                    <AdminNotificationsDryRunPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications/test-send"
                element={
                  <ProtectedRoute permission="notifications:dry_run">
                    <AdminNotificationsTestSendPage />
                  </ProtectedRoute>
                }
              />

              {/* F28 — Email suppressions admin queue. Read gates the page;
                  write actions on rows are additionally gated on
                  `email_suppressions:write` (super_admin + support only). */}
              <Route
                path="/email-suppressions"
                element={
                  <ProtectedRoute permission="email_suppressions:read">
                    <EmailSuppressionsPage />
                  </ProtectedRoute>
                }
              />

              {/* F27 (Wave 2E) — WhatsApp admin tooling: templates registry
                  + quality state. Read gates both pages; Sync action on the
                  templates page is additionally gated client-side on
                  `whatsapp:write` (super_admin only); the server re-enforces. */}
              <Route
                path="/whatsapp/templates"
                element={
                  <ProtectedRoute permission="whatsapp:read">
                    <WhatsAppTemplatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/whatsapp/quality"
                element={
                  <ProtectedRoute permission="whatsapp:read">
                    <WhatsAppQualityPage />
                  </ProtectedRoute>
                }
              />

              {/* F29 — Teams moderation. Read gates the page; moderation
                  actions (Suspend / Reinstate / Disband) on the detail page
                  are additionally gated client-side on `teams:moderate`
                  (super_admin + content_moderator); server re-enforces. */}
              <Route
                path="/teams"
                element={
                  <ProtectedRoute permission="teams:read">
                    <AdminTeamsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams/:teamId"
                element={
                  <ProtectedRoute permission="teams:read">
                    <AdminTeamDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F31 — Skill-based matching. Skill-disputes queue + detail
                  (resolve gated on `skill_disputes:resolve`), sport-skill-
                  descriptors CMS (read gates the page; write actions gated
                  inside on `sport_skill_descriptors:write`), and the per-user
                  skill-ratings sub-tab (override gated inside on
                  `skill_ratings:override` — super_admin only). */}
              <Route
                path="/skill-disputes"
                element={
                  <ProtectedRoute permission="skill_disputes:resolve">
                    <AdminSkillDisputesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/skill-disputes/:id"
                element={
                  <ProtectedRoute permission="skill_disputes:resolve">
                    <AdminSkillDisputeDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sport-skill-descriptors"
                element={
                  <ProtectedRoute permission="sport_skill_descriptors:read">
                    <AdminSportSkillDescriptorsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users/:userId/skill-ratings"
                element={
                  <ProtectedRoute permission="users:read">
                    <AdminUserSkillRatingsPage />
                  </ProtectedRoute>
                }
              />

              {/* F32 — Open games moderation. List + detail (suspend/unsuspend
                  gated inside on `open_games:moderate`) + report queue (resolve
                  gated inside on `open_games:moderate`). Read-gated on
                  `open_games:read` (super_admin + support + content_moderator). */}
              <Route
                path="/open-games"
                element={
                  <ProtectedRoute permission="open_games:read">
                    <AdminOpenGamesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/open-games/:id"
                element={
                  <ProtectedRoute permission="open_games:read">
                    <AdminOpenGameDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/open-game-reports"
                element={
                  <ProtectedRoute permission="open_games:read">
                    <AdminOpenGameReportsPage />
                  </ProtectedRoute>
                }
              />

              {/* F33 — Trainer cert-verification queue + moderation detail.
                  Read gates both pages; the detail page's Approve/Reject +
                  per-cert verify/reject/revoke + Suspend/Reinstate/
                  Force-deactivate actions all share the single
                  `trainer_verifications:review` permission. Server re-enforces. */}
              <Route
                path="/trainer-verifications"
                element={
                  <ProtectedRoute permission="trainer_verifications:review">
                    <TrainerVerificationsQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trainer-verifications/:userId"
                element={
                  <ProtectedRoute permission="trainer_verifications:review">
                    <TrainerVerificationDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F34 — Trainer package-consumption reversal worklist. The
                  single F34 admin surface: lookup a consumption by id + reverse
                  the completed/no-show decrement (credits the session back).
                  Read + write gated on the single
                  `trainer_package_consumption:reverse` permission (super_admin +
                  support); the server re-enforces + audits via the F06 onSend
                  hook. */}
              <Route
                path="/trainer-package-consumptions"
                element={
                  <ProtectedRoute permission="trainer_package_consumption:reverse">
                    <TrainerPackageConsumptionsPage />
                  </ProtectedRoute>
                }
              />

              {/* F35 — per-trainer billing: view active plan + effective rate +
                  history + inline invoices (gated `payments:read`); the override
                  CTA inside is additionally gated on `trainer_billing:manage`
                  (super_admin + finance). Server re-enforces. */}
              <Route
                path="/trainers/:trainerUserId/billing"
                element={
                  <ProtectedRoute permission="payments:read">
                    <AdminTrainerBillingPage />
                  </ProtectedRoute>
                }
              />

              {/* F36 — Club moderation (trainer-managed teams / club archetype).
                  Read gates both pages; the detail page's Verify / Suspend /
                  Reinstate actions are additionally gated client-side on
                  `clubs:moderate` (super_admin + content_moderator); server
                  re-enforces. */}
              <Route
                path="/clubs"
                element={
                  <ProtectedRoute permission="clubs:read">
                    <AdminClubsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clubs/:clubId"
                element={
                  <ProtectedRoute permission="clubs:read">
                    <AdminClubDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* F37 — Player↔trainer recommendations. Badge + review-tag
                  catalog CMS (read gates the pages; Add/Edit/Delete actions
                  are additionally gated inside on `recommendation_catalog:write`).
                  Moderation queue + detail (hide/remove/restore/edit/lock gated
                  on `reviews:moderate`; endorsement remove/restore additionally
                  on `endorsements:moderate`). Server re-enforces + audits. */}
              <Route
                path="/recommendation-catalog/badges"
                element={
                  <ProtectedRoute permission="recommendation_catalog:read">
                    <AdminBadgeCatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recommendation-catalog/tags"
                element={
                  <ProtectedRoute permission="recommendation_catalog:read">
                    <AdminReviewTagsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/review-moderation"
                element={
                  <ProtectedRoute permission="reviews:moderate">
                    <ReviewModerationQueuePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/review-moderation/:id"
                element={
                  <ProtectedRoute permission="reviews:moderate">
                    <ReviewModerationDetailPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Fallback — redirect unknown paths to the dashboard. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ToastProvider>
  );
}
